<template>
    <Spinner v-if="displaySpinner" />
    <InfoMessage
        title="Define your value proposition"
        storageKey="welcome-message-product"
        :forceShow="!hasAnyContent"
        showToggle
    >
        <p>
            Products and services represent the value your domain delivers. This
            value is realized through the execution of well-defined processes.
        </p>
        <p v-if="processes.length === 0">
            Bring your product to life by creating your first process. Processes
            map out the journey users take to achieve their goals.
        </p>
    </InfoMessage>
    <Page>
        <template #title>
            <EditableTitle v-model="product" icon="pi pi-box" />
        </template>

        <template #controls>
            <LastChangedInfo
                v-if="product?.identity"
                :entityId="product.identity"
                :entityType="EntityType.PRODUCT"
            />
            <Button
                v-if="description && !description.content && isEditing"
                v-tooltip.bottom="{
                    value: 'Will generate a first draft of the description based on the context.',
                    showDelay: 100,
                }"
                severity="secondary"
                icon="pi pi-sparkles"
                label="Generate first draft"
                @click="generateProductDescription"
            />
        </template>
        <Section class="description" editable>
            <TextAppArtefact
                v-if="product?.identity && description"
                v-model="description"
                :entityId="product.identity"
                :entityType="EntityType.PRODUCT"
                :isEditing="isEditing"
                :stekzModel="product"
                ref="textAppArtefact"
            />
        </Section>

        <Section class="processes-Section" editable ref="processesSectionRef">
            <Processes :isEditing="isEditing" :processes="processes" />
        </Section>

        <Section class="documents-Section" editable>
            <Documents
                v-if="product"
                entityType="product"
                :entity="product"
                :isEditing="isEditing"
                :artefacts="artefacts"
            />
        </Section>
    </Page>

    <HintPopover
        v-if="currentHintStep"
        :current-step="currentHintStep"
        :is-last-step="isLastHintStep"
        @next="nextHintStep"
        @dismiss-all="dismissAllHints"
    />
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import Documents from "@/components/Documents.vue";
import EditableTitle from "@/components/EditableTitle.vue";
import InfoMessage from "@/components/InfoMessage.vue";
import LastChangedInfo from "@/components/LastChangedInfo.vue";
import Page from "@/components/Page.vue";
import Processes from "@/components/Processes/ProcessesList.vue";
import Section from "@/components/Section.vue";
import TextAppArtefact from "@/components/TextAppArtefact.vue";
import HintPopover from "@/components/hints/HintPopover.vue";
import { useEditManager } from "@/composables/edit-manager";
import { useGenerateDescriptionDialog } from "@/composables/generate-description";
import { useProductManager } from "@/composables/product-manager";
import { usePageHints } from "@/composables/use-page-hints";
import { useArtefactsManager } from "@/lib/artefacts/user";
import Spinner from "@/shared/Spinner.vue";
import { EntityType } from "@/stores/entity-store";
import { useProcessesManager } from "@/stores/processes-store";
import { useTaskManager } from "@/stores/task-store";

const processesSectionRef = ref<HTMLElement | null>(null);
const {
    currentStep: currentHintStep,
    isLastStep: isLastHintStep,
    nextStep: nextHintStep,
    dismissAll: dismissAllHints,
} = usePageHints([
    {
        id: "product-add-process",
        targetRef: processesSectionRef,
        title: "Bring your product to life",
        content:
            "Processes map out how your product or service gets delivered. Add one to start modeling the journey.",
    },
]);

const { artefacts } = useArtefactsManager();
useTaskManager();
const { isEditing } = useEditManager();
const { processes } = useProcessesManager();
const { product, isFetching, description } = useProductManager();
const { generateContent } = useGenerateDescriptionDialog();

const displaySpinner = ref(false);

watch(isFetching, (newValue) => (displaySpinner.value = newValue), {
    immediate: true,
});

const generateProductDescription = async () => {
    if (!product.value) {
        return;
    }
    await generateContent(
        product.value.identity,
        EntityType.PRODUCT,
        description.value?.updateContent.bind(description.value)
    );
};

const hasAnyContent = computed(() => {
    return Boolean(
        processes.value.length > 0 ||
            description.value?.content ||
            artefacts.value?.length > 0
    );
});
</script>
